/**
 * `useCursorList` — the client half of the KEYSET feed convention
 * (docs/design/LIST-PAGINATION-AUDIT.md). The "load more" counterpart to
 * `usePagedList`: for deep, append-only feeds (worker runs, events, activity)
 * it accumulates pages via an opaque cursor instead of page numbers, so there's
 * no total and no "jump to page N" — just `rows`, `hasMore`, and `loadMore()`.
 *
 *   const { rows, hasMore, loadMore, isLoadingMore } = useCursorList(agentRuns.feed, {
 *     query: { role }, limit: 30,
 *   });
 *   return (<><RunsTable rows={rows} />
 *     {hasMore && <Button onClick={loadMore} disabled={isLoadingMore}>Load more</Button>}</>);
 *
 * Built on `useSWRInfinite`: each page's key is the resolved URL carrying the
 * previous page's `next_cursor`, so pages cache independently and changing a
 * filter resets the feed. Pairs with `keysetSchema` / `buildKeyset` server-side.
 */

import * as React from "react";
import useSWRInfinite from "swr/infinite";
import type { SWRInfiniteConfiguration } from "swr/infinite";
import { defaultFetcher } from "./client";
import { buildUrl, type Operation } from "./types";
import {
  DEFAULT_FEED_LIMIT,
  type KeysetQuery,
  type KeysetResult,
} from "./contracts/_common";

export interface UseCursorListOptions<TQuery> {
  /** Filters merged into every page request (cursor/limit are managed here). */
  query?: Omit<TQuery, "cursor" | "limit">;
  /** Rows per page. Default 30. */
  limit?: number;
  /** Pass false to skip the request. */
  enabled?: boolean;
  swr?: SWRInfiniteConfiguration;
}

export interface UseCursorListResult<Item> {
  /** All rows loaded so far, in feed order. */
  rows: Item[];
  /** Another page is available. */
  hasMore: boolean;
  /** Fetch the next page (appends to `rows`). */
  loadMore: () => void;
  /** First page is loading (nothing shown yet). */
  isLoading: boolean;
  /** A subsequent page is loading (rows already shown). */
  isLoadingMore: boolean;
  error: unknown;
  /** Pages loaded so far (SWRInfinite size). */
  size: number;
  /** Revalidate the whole feed (e.g. after a live signal). */
  refresh: () => void;
}

export function useCursorList<TQuery extends KeysetQuery, Item>(
  op: Operation<void, TQuery, void, KeysetResult<Item>>,
  options?: UseCursorListOptions<TQuery>,
): UseCursorListResult<Item> {
  const limit = options?.limit ?? DEFAULT_FEED_LIMIT;
  const enabled = options?.enabled ?? true;
  const filters = options?.query;
  // Stable filter identity so the key sequence only resets on a real change.
  const filterKey = JSON.stringify(filters ?? {});

  const getKey = React.useCallback(
    (index: number, prev: KeysetResult<Item> | null): string | null => {
      if (!enabled) return null;
      if (prev && !prev.has_more) return null; // feed exhausted
      const cursor = index === 0 ? undefined : (prev?.next_cursor ?? undefined);
      if (index > 0 && !cursor) return null; // defensive: no cursor → stop
      return buildUrl(op, undefined as void, {
        ...(filters ?? {}),
        cursor,
        limit,
      } as TQuery);
    },
    // filters captured via filterKey; op/limit/enabled are stable per render set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [op, filterKey, limit, enabled],
  );

  const swr = useSWRInfinite<KeysetResult<Item>>(
    getKey,
    defaultFetcher as (url: string) => Promise<KeysetResult<Item>>,
    options?.swr,
  );

  const pages = (swr.data ?? []) as KeysetResult<Item>[];
  const rows = React.useMemo(() => pages.flatMap((p) => p.rows), [pages]);
  const lastPage = pages[pages.length - 1];
  // Before the first page resolves, assume more is possible (so an initial
  // "load more" isn't hidden); after, trust the server's `has_more`.
  const hasMore = lastPage ? lastPage.has_more : false;

  const isLoading = !swr.data && !swr.error && enabled;
  const isLoadingMore =
    swr.isValidating && pages.length > 0 && pages.length < swr.size;

  return {
    rows,
    hasMore,
    loadMore: () => void swr.setSize((s) => s + 1),
    isLoading,
    isLoadingMore,
    error: swr.error,
    size: swr.size,
    refresh: () => void swr.mutate(),
  };
}
